/**
 * React Query hooks for the global artifacts system.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  uploadArtifact,
  captureLink,
  unlinkArtifact,
  getArtifactSignedUrl,
  getArtifactSignedUrls,
  type UploadArtifactParams,
  type CaptureLinkParams,
  type ArtifactRecord,
} from '@/lib/artifacts/artifactUpload';
import type { LinkEntityType, LinkRole } from '@/lib/artifacts/constants';

// ---------- Types ----------

export interface ArtifactWithUrl extends ArtifactRecord {
  signedUrl: string | null;
  linkId: string;
  linkRole: string;
}

// ---------- Keys ----------

const artifactKeys = {
  byEntity: (entityType: string, entityId: string) =>
    ['artifacts', 'entity', entityType, entityId] as const,
  unlinked: (shopId: string) => ['artifacts', 'unlinked', shopId] as const,
};

// ---------- Fetch artifacts for an entity ----------

export function useEntityArtifacts(
  entityType: LinkEntityType | undefined,
  entityId: string | undefined,
  shopId: string | undefined,
) {
  return useQuery({
    queryKey: artifactKeys.byEntity(entityType || '', entityId || ''),
    enabled: !!entityType && !!entityId && !!shopId,
    queryFn: async (): Promise<ArtifactWithUrl[]> => {
      // Fetch links for this entity
      const { data: links, error: linkErr } = await supabase
        .from('artifact_links')
        .select('id, artifact_id, role')
        .eq('entity_type', entityType!)
        .eq('entity_id', entityId!)
        .eq('shop_id', shopId!);

      if (linkErr) throw linkErr;
      if (!links || links.length === 0) return [];

      const artifactIds = links.map((l: any) => l.artifact_id);

      // Fetch artifact rows
      const { data: artifacts, error: artErr } = await supabase
        .from('artifacts')
        .select('*')
        .in('id', artifactIds)
        .eq('status', 'active');

      if (artErr) throw artErr;
      if (!artifacts) return [];

      // V-E-2 R151-HIGH-2: batch signed-URL generation. Previously this was
      // N round-trips (one createSignedUrl per uploaded_blob artifact inside
      // a Promise.all map). For an entity with many artifacts attached this
      // multiplied storage round trips. The plural API issues all signed
      // URLs in a single call.
      const blobPaths = (artifacts as any[])
        .filter(a => a.storage_kind === 'uploaded_blob' && a.storage_path)
        .map(a => a.storage_path as string);
      const signedUrlByPath = await getArtifactSignedUrls(blobPaths);

      const results: ArtifactWithUrl[] = (artifacts as any[]).map((a) => {
        const link = links.find((l: any) => l.artifact_id === a.id);
        const signedUrl =
          a.storage_kind === 'uploaded_blob' && a.storage_path
            ? signedUrlByPath.get(a.storage_path) || null
            : null;
        return {
          ...a,
          signedUrl,
          linkId: link?.id || '',
          linkRole: (link as any)?.role || '',
        };
      });

      return results;
    },
  });
}

// ---------- Upload mutation ----------

export function useUploadArtifact() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (params: UploadArtifactParams) => uploadArtifact(params),
    onSuccess: (_data, variables) => {
      if (variables.link) {
        qc.invalidateQueries({
          queryKey: artifactKeys.byEntity(
            variables.link.entityType,
            variables.link.entityId,
          ),
        });
      }
    },
  });
}

// ---------- Link capture mutation ----------

export function useCaptureLinkArtifact() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (params: CaptureLinkParams) => captureLink(params),
    onSuccess: (_data, variables) => {
      if (variables.link) {
        qc.invalidateQueries({
          queryKey: artifactKeys.byEntity(
            variables.link.entityType,
            variables.link.entityId,
          ),
        });
      }
    },
  });
}

// ---------- Unlink mutation ----------

export function useUnlinkArtifact(entityType?: LinkEntityType, entityId?: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (linkId: string) => unlinkArtifact(linkId),
    onSuccess: () => {
      if (entityType && entityId) {
        qc.invalidateQueries({
          queryKey: artifactKeys.byEntity(entityType, entityId),
        });
      }
    },
  });
}

// ---------- Unlinked artifacts (future inbox) ----------

export function useUnlinkedArtifacts(shopId: string | undefined) {
  return useQuery({
    queryKey: artifactKeys.unlinked(shopId || ''),
    enabled: !!shopId,
    queryFn: async () => {
      // Use NOT EXISTS via RPC or two-step query
      const { data: allArtifacts, error: artErr } = await supabase
        .from('artifacts')
        .select('id')
        .eq('shop_id', shopId!)
        .eq('status', 'active');

      if (artErr) throw artErr;
      if (!allArtifacts || allArtifacts.length === 0) return [];

      const ids = allArtifacts.map((a: any) => a.id);

      const { data: linkedIds, error: linkErr } = await supabase
        .from('artifact_links')
        .select('artifact_id')
        .in('artifact_id', ids);

      if (linkErr) throw linkErr;

      const linkedSet = new Set((linkedIds || []).map((l: any) => l.artifact_id));
      const unlinkedIds = ids.filter((id: string) => !linkedSet.has(id));

      if (unlinkedIds.length === 0) return [];

      const { data, error } = await supabase
        .from('artifacts')
        .select('*')
        .in('id', unlinkedIds)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
}
